import { useMemo } from 'react';
import { TaskTableDataType } from '../components/TaskTable/types/TaskTableTypes';
import {
  applySearchAndFiltersWithParents,
  buildGroupTasks,
  removeChildContextParents,
} from '../components/TaskManagement/helpers/filterHelpers';
import { Column } from '../types/WorkspaceViewColumn';
import { WorkspaceAccessResponse } from '../api/workspace';
import { TaskStatus } from '../types/TaskStatus';
import { PriorityOptions, TaskEstimateMapping } from '../components/TaskTable/helpers/helper';
import Workspace, { WorkspaceEstimationType, WorkspaceFramework } from '../types/Workspace';
import { Filter } from '../components/Modal/TaskFiltersModal';
import { Epic } from '../types/Epic';
import { User } from '../types/User';
import { TaskType } from '../types/TaskType';
import { UseQueryResult } from '@tanstack/react-query';
import { getEffectiveStage } from '../utils/taskListUtils';

interface UseTaskFilteringProps {
  dataSource: TaskTableDataType[];
  selectedFilters: Filter[];
  searchValue: string;
  groupBy: Column;
  currentUserId?: number;
  activeSprintId?: number;
  sprintStatusFilter?: Filter;
  epicFilter?: Filter;
  ownerFilter?: Filter;
  statusFilter?: Filter;
  deadlineFilter?: Filter;
  priorityFilter?: Filter;
  estimateFilter?: Filter;
  epics?: Epic[];
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  workspaceQuery: UseQueryResult<Workspace, Error>;
  framework?: WorkspaceFramework;
  // Display setting has subitems turned off: drop parents shown only as context for a
  // matching subitem, since that subitem is hidden anyway. #2324
  hideSubitems?: boolean;
}

// Type for a single group in tasksPerGroup
export type TaskGroup = {
  group: TaskType | Epic | User | TaskStatus | PriorityOptions | number | string;
  tasks: TaskTableDataType[];
};

const isEpic = (epic: unknown): epic is Epic => typeof epic === 'object' && epic !== null && 'id' in epic;

const isUser = (user: unknown): user is User => typeof user === 'object' && user !== null && 'id' in user;

const isTaskStatus = (status: unknown): status is TaskStatus =>
  typeof status === 'object' && status !== null && 'id' in status;

export const useTaskFiltering = ({
  dataSource,
  selectedFilters,
  searchValue,
  groupBy,
  currentUserId,
  activeSprintId,
  sprintStatusFilter,
  epicFilter,
  ownerFilter,
  statusFilter,
  deadlineFilter,
  priorityFilter,
  estimateFilter,
  epics,
  usersWithAccessToSpace,
  workspaceQuery,
  framework,
  hideSubitems = false,
}: UseTaskFilteringProps) => {
  const noGrouping = !groupBy || (groupBy as string) === 'none';
  const unassignedTasks = useMemo(() => {
    // #2309: when grouping by owner, an unassigned subitem belongs in the Unassigned
    // group even if its parent is assigned (the parent is borrowed in as dimmed context).
    // Mirror the per-group placement instead of filtering parents up front.
    if (!noGrouping && groupBy === Column.assignee) {
      const narrowed = applySearchAndFiltersWithParents(
        selectedFilters,
        dataSource,
        searchValue,
        currentUserId,
        workspaceQuery.data?.estimationType,
        activeSprintId
      );
      const grouped = buildGroupTasks(narrowed, (t) => !t.assignee);
      return hideSubitems ? removeChildContextParents(grouped) : grouped;
    }

    const filtered = applySearchAndFiltersWithParents(
      selectedFilters,
      dataSource.filter((t) => {
        if (noGrouping) return true;
        switch (groupBy) {
            case Column.epic:
              return !t.epicId;
            case Column.assignee:
              return !t.assignee;
            case Column.status:
              return !t.status;
            case Column.deadline:
              return !t.deadline;
            case Column.priority:
              return !t.priority;
            case Column.estimate:
              return !t.estimate;
            case Column.sprintStatus: {
              const stage = getEffectiveStage(t, activeSprintId);
              return stage !== 'sprint' && stage !== 'planning';
            }
            case Column.label:
              return !t.TaskLabels || t.TaskLabels.length === 0;
            case Column.startDate:
              return !t.startDate;
            default:
              return false;
          }
        }),
      searchValue,
      currentUserId,
      workspaceQuery.data?.estimationType,
      activeSprintId
    );
    return hideSubitems ? removeChildContextParents(filtered) : filtered;
  }, [
    dataSource,
    selectedFilters,
    searchValue,
    currentUserId,
    groupBy,
    activeSprintId,
    noGrouping,
    workspaceQuery.data,
    hideSubitems,
  ]);

  const estimationType = workspaceQuery.data?.estimationType ?? WorkspaceEstimationType.storyPoints;

  const tasksDataSource = applySearchAndFiltersWithParents(
    selectedFilters,
    dataSource,
    searchValue,
    currentUserId,
    estimationType,
    activeSprintId
  );

  const tasksPerGroup: TaskGroup[] = useMemo(() => {
    const selectedFiltersTypes =
      selectedFilters && Array.isArray(selectedFilters) ? selectedFilters?.map((filter) => filter.type) : [];

    const groups: TaskGroup[] = ((): TaskGroup[] => {
      switch (groupBy) {
      case Column.taskType: {
        const filteredTaskTypes = Object.values(TaskType).filter((taskType) => taskType !== TaskType.epic);
        return filteredTaskTypes.map((taskType) => ({
          group: taskType,
          tasks: tasksDataSource.filter((t) => t.type === taskType),
        }));
      }
      case Column.epic: {
        const filteredEpics =
          epicFilter && selectedFiltersTypes.includes('epic')
            ? epicFilter.options
                .filter((option) => option.selected && option.name !== 'No Epic Assigned')
                .map((option) => option.metadata)
            : (epics ?? []);
        return filteredEpics.filter(isEpic).map((epic) => ({
          group: epic,
          tasks: tasksDataSource.filter((t) => t.epicId === epic.id),
        }));
      }
      case Column.assignee: {
        const filteredUsers =
          ownerFilter && selectedFiltersTypes.includes('owner')
            ? ownerFilter.options
                .filter((option) => option.selected)
                .map((option) =>
                  option.metadata
                    ? option.metadata
                    : option.name === 'Me'
                      ? (usersWithAccessToSpace?.find((access) => access.User.id === currentUserId)?.User ?? null)
                      : null
                )
                .filter(Boolean)
            : (usersWithAccessToSpace?.map((access) => access.User) ?? []);
        return filteredUsers
          .filter(isUser)
          .sort((a, b) => {
            const nameA = a.fullName || a.email || '';
            const nameB = b.fullName || b.email || '';
            return nameA.localeCompare(nameB);
          })
          .map((user) => ({
            group: user,
            // #2309: place subitems in their own owner's group; a parent owned by
            // someone else is borrowed in as dimmed, read-only context.
            tasks: buildGroupTasks(tasksDataSource, (t) => t.assignee === user.id),
          }));
      }
      case Column.status: {
        const filteredStatuses = (
          statusFilter && selectedFiltersTypes.includes('status')
            ? statusFilter.options
                .filter((option) => option.selected && option.metadata)
                .map((option) => option.metadata)
            : (workspaceQuery.data?.TaskStatus ?? [])
        )
          .filter(isTaskStatus)
          .sort((a, b) => {
            const groupOrder = ['notStarted', 'inProgress', 'completed'];
            const groupComparison = groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group);
            return groupComparison !== 0 ? groupComparison : (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id);
          });
        return filteredStatuses.map((status) => ({
          group: status,
          tasks: tasksDataSource.filter((t) => t.statusId === status.id),
        }));
      }
      case Column.estimate: {
        const filteredEstimates =
          estimateFilter && selectedFiltersTypes.includes('estimate')
            ? estimateFilter.options
                .filter((option) => option.selected && option.metadata)
                .map((option) => option.metadata as number)
            : Object.keys(TaskEstimateMapping(estimationType));
        return filteredEstimates.map((estimate) => ({
          group: Number(estimate),
          tasks: tasksDataSource.filter((t) =>
            estimationType === WorkspaceEstimationType.tshirtSizes
              ? t.estimate === Number(estimate)
              : t.storyPoints === Number(estimate)
          ),
        }));
      }
      case Column.priority: {
        const filteredPriorities = (
          priorityFilter && selectedFiltersTypes.includes('priority')
            ? priorityFilter.options
                .filter((option) => option.selected && option.metadata)
                .map((option) => option.metadata as PriorityOptions)
            : [
                PriorityOptions.NoPriority,
                PriorityOptions.Low,
                PriorityOptions.Medium,
                PriorityOptions.High,
                PriorityOptions.Urgent,
              ]
        ).sort((a, b) => {
          const priorityOrder = [
            PriorityOptions.Urgent,
            PriorityOptions.High,
            PriorityOptions.Medium,
            PriorityOptions.Low,
            PriorityOptions.NoPriority,
          ];
          return priorityOrder.indexOf(a) - priorityOrder.indexOf(b);
        });
        return filteredPriorities.map((priority) => ({
          group: priority,
          tasks: tasksDataSource.filter((t) => t.priority === priority),
        }));
      }
      case Column.startDate: {
        const groupedByStartDate = tasksDataSource
          .filter((t) => t.startDate)
          .sort(
            (a, b) =>
              (b.startDate ? new Date(b.startDate).getTime() : 0) - (a.startDate ? new Date(a.startDate).getTime() : 0)
          )
          .reduce(
            (acc, task) => {
              const startDate = task.startDate
                ? `${new Date(task.startDate).toLocaleString('default', { month: 'long' })} ${new Date(
                    task.startDate
                  ).getFullYear()}`
                : 'No Start Date';
              if (!acc[startDate]) {
                acc[startDate] = [];
              }
              acc[startDate].push(task);
              return acc;
            },
            {} as Record<string, TaskTableDataType[]>
          );

        return Object.entries(groupedByStartDate).map(([startDate, tasks]) => ({
          group: startDate,
          tasks,
        }));
      }
      case Column.deadline: {
        const groupedByDeadline = tasksDataSource
          .filter((t) => t.deadline)
          .sort(
            (a, b) =>
              (b.deadline ? new Date(b.deadline).getTime() : 0) - (a.deadline ? new Date(a.deadline).getTime() : 0)
          )
          .reduce(
            (acc, task) => {
              const deadline = task.deadline
                ? `${new Date(task.deadline).toLocaleString('default', { month: 'long' })} ${new Date(
                    task.deadline
                  ).getFullYear()}`
                : 'No Deadline';
              if (!acc[deadline]) {
                acc[deadline] = [];
              }
              acc[deadline].push(task);
              return acc;
            },
            {} as Record<string, TaskTableDataType[]>
          );

        return Object.entries(groupedByDeadline).map(([deadline, tasks]) => ({
          group: deadline,
          tasks,
        }));
      }
      case Column.sprintStatus: {
        // Group by the SAME stage the Stage column renders. Reading planningSessionId/
        // sprintId here was a second source of truth: planning membership is keyed off
        // `stage === 'planning'` (PlanningSession.tsx:62), and a task set to Planning from
        // the Stage dropdown never gets a planningSessionId, so it fell out of this
        // group while still showing a Planning chip. #2970 (same cause as #2969)
        const inStage = (stage: 'planning' | 'sprint') =>
          tasksDataSource.filter((t) => getEffectiveStage(t, activeSprintId) === stage);

        if (framework === WorkspaceFramework.scrum) {
          return [
            { group: 'planning', tasks: inStage('planning') },
            { group: 'sprint', tasks: inStage('sprint') },
          ];
        } else if (framework === WorkspaceFramework.kanban) {
          // `getEffectiveStage` is framework-independent: kanban membership is stage 'sprint'.
          return [{ group: 'kanban', tasks: inStage('sprint') }];
        }
        return [];
      }
      case Column.label: {
        // Get workspace labels
        const workspaceLabels = workspaceQuery.data?.Labels || [];

        // Get all unique label IDs from tasks
        const usedLabelIds = new Set<number>();
        tasksDataSource.forEach((task) => {
          if (task.TaskLabels && task.TaskLabels.length > 0) {
            task.TaskLabels.forEach((taskLabel) => {
              usedLabelIds.add(taskLabel.labelId);
            });
          }
        });

        // Filter workspace labels to only those used by tasks
        const usedLabels = workspaceLabels
          .filter((label) => usedLabelIds.has(label.id))
          .sort((a, b) => a.orderIndex - b.orderIndex);

        // Create groups for each used label
        return usedLabels.map((label) => ({
          group: label,
          tasks: tasksDataSource.filter(
            (t) => t.TaskLabels && t.TaskLabels.some((taskLabel) => taskLabel.labelId === label.id)
          ),
        }));
      }
      default:
        return [];
    }
    })();

    // Hide empty groups when the user is actively narrowing results (search or filters).
    // Without narrowing, empty groups remain visible so DnD targets and group structure are preserved.
    const visibleGroups = hideSubitems
      ? groups.map((g) => ({ ...g, tasks: removeChildContextParents(g.tasks) }))
      : groups;
    const isNarrowing = searchValue.length > 0 || (selectedFilters?.length ?? 0) > 0;
    return isNarrowing ? visibleGroups.filter((g) => g.tasks.length > 0) : visibleGroups;
  }, [
    dataSource,
    selectedFilters,
    searchValue,
    currentUserId,
    groupBy,
    activeSprintId,
    epicFilter,
    ownerFilter,
    statusFilter,
    deadlineFilter,
    priorityFilter,
    estimateFilter,
    epics,
    usersWithAccessToSpace,
    workspaceQuery.data,
    framework,
    hideSubitems,
  ]);

  const activeTasks = useMemo(() => {
    const tasks = applySearchAndFiltersWithParents(
      selectedFilters,
      dataSource,
      searchValue,
      currentUserId,
      workspaceQuery.data?.estimationType,
      activeSprintId
    );
    return hideSubitems ? removeChildContextParents(tasks) : tasks;
  }, [
    dataSource,
    selectedFilters,
    searchValue,
    currentUserId,
    workspaceQuery.data?.estimationType,
    activeSprintId,
    hideSubitems,
  ]);

  return {
    unassignedTasks,
    activeTasks,
    tasksPerGroup,
  };
};
